import { Icon } from '@components/Icon.tsx';
import { IconType } from '@src/IconType.ts';
import {
  Fragment,
  type MouseEvent,
  type ReactElement,
  Suspense,
  lazy,
  useCallback,
  useState,
} from 'react';
import { classNames } from '@utils/classNames.ts';
import { focusStyle } from '@components/input/utils.ts';
import { Tooltip } from './base/Tooltip';
import { Body2 } from './typography/Body2';

export default function TrashButton({
  onClick,
  className,
  testId,
  showConfirmation,
  confirmationMessage,
  passThrough,
  tooltip,
}: {
  readonly onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  readonly className?: string;
  readonly testId?: string;
  readonly showConfirmation?: boolean;
  readonly confirmationMessage?: string;
  readonly passThrough?: {
    icon?: { className?: string };
  };
  readonly tooltip?: string;
}) {
  const [open, setOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const onClickInternal = showConfirmation
    ? (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        setOpen(true);
      }
    : onClick;

  const WrapperComp = useCallback(
    ({ children }: { children: ReactElement }) => {
      if (tooltip) {
        return (
          <Tooltip
            className="bg-slate-700 rounded-lg text-center"
            content={
              <Body2 className="text-white text-sm font-normal">
                {tooltip}
              </Body2>
            }
            placement="top"
            handler={
              tooltip ? (open: boolean) => setTooltipOpen(open) : undefined
            }
            open={tooltip ? tooltipOpen : false}
          >
            {children}
          </Tooltip>
        );
      }
      return <Fragment>{children}</Fragment>;
    },
    [tooltip, tooltipOpen],
  );

  return (
    <>
      <WrapperComp>
        <button
          className={classNames(
            'flex items-center justify-center h-[20px] w-[20px] p-0.5 rounded bg-gray-100 hover:bg-white cursor-pointer disabled:cursor-not-allowed',
            focusStyle,
            className,
          )}
          onClick={onClickInternal}
          data-testid={testId}
        >
          <Icon
            type={IconType.TrashAlt}
            className={classNames(
              'h-full w-full',
              passThrough?.icon?.className,
            )}
            aria-hidden="true"
          />
        </button>
      </WrapperComp>
      {showConfirmation && (
        <Suspense>
          <DeleteConfirmation
            open={open}
            setOpen={setOpen}
            onConfirm={onClick}
            confirmationMessage={confirmationMessage}
          />
        </Suspense>
      )}
    </>
  );
}

const DeleteConfirmation = lazy(() =>
  import('@components/modals/DeleteConfirmation').then((result) => ({
    default: result.DeleteConfirmation,
  })),
);
